/**
 * API client.
 *
 * Access tokens last 15 minutes; refresh tokens rotate on every use. A 401 that
 * says the token expired triggers exactly one refresh and a retry — concurrent
 * requests share that single refresh, because firing several would replay the
 * same refresh token and the server treats a replay as theft and revokes the
 * whole session family.
 */

const BASE = '/api'
const ACCESS = 'therm.admin.access'
const REFRESH = 'therm.admin.refresh'

export const tokens = {
  get access() { return localStorage.getItem(ACCESS) },
  get refresh() { return localStorage.getItem(REFRESH) },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS, access)
    localStorage.setItem(REFRESH, refresh)
  },
  clear() {
    localStorage.removeItem(ACCESS)
    localStorage.removeItem(REFRESH)
  },
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

let refreshing: Promise<boolean> | null = null

async function refreshTokens(): Promise<boolean> {
  // One in flight at a time. See the note at the top of this file.
  if (refreshing) return refreshing
  refreshing = (async () => {
    const token = tokens.refresh
    if (!token) return false
    try {
      const r = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: token }),
      })
      if (!r.ok) { tokens.clear(); return false }
      const body = await r.json()
      tokens.set(body.access_token, body.refresh_token)
      return true
    } catch {
      return false
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  if (tokens.access) headers.set('Authorization', `Bearer ${tokens.access}`)

  const res = await fetch(`${BASE}${path}`, { ...init, headers })

  if (res.status === 401 && retry) {
    if (await refreshTokens()) return api<T>(path, init, false)
    tokens.clear()
    window.location.hash = '#/login'
    throw new ApiError(401, 'signed out')
  }
  if (!res.ok) {
    let detail = res.statusText
    try { detail = (await res.json()).detail ?? detail } catch { /* not json */ }
    throw new ApiError(res.status, detail)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export async function login(email: string, password: string) {
  const r = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, platform: 'admin' }),
  })
  if (!r.ok) throw new ApiError(r.status, 'invalid credentials')
  const body = await r.json()
  tokens.set(body.access_token, body.refresh_token)
  return body
}

export async function logout() {
  const token = tokens.refresh
  tokens.clear()
  if (token) {
    await fetch(`${BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: token }),
    }).catch(() => {})
  }
}

/**
 * Download a file from an authenticated endpoint.
 *
 * A plain `<a href>` cannot do this: the browser sends no Authorization header,
 * so the request arrives unauthenticated and the operator downloads a 401. The
 * response is fetched with the usual token handling, turned into a blob and
 * handed to a synthetic link, which also lets the server's
 * Content-Disposition filename survive.
 */
export async function download(path: string, retry = true): Promise<void> {
  const headers = new Headers()
  if (tokens.access) headers.set('Authorization', `Bearer ${tokens.access}`)

  const res = await fetch(`${BASE}${path}`, { headers })
  if (res.status === 401 && retry) {
    if (await refreshTokens()) return download(path, false)
    tokens.clear()
    window.location.hash = '#/login'
    throw new ApiError(401, 'signed out')
  }
  if (!res.ok) throw new ApiError(res.status, res.statusText)

  const cd = res.headers.get('Content-Disposition') ?? ''
  const name = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? 'download.csv'
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoking immediately races the download in Safari; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
